import type {
  HarnessActionApplyResult,
  HarnessPendingActionStatus,
} from '@erp/rpc';

export type PreviewCardStatus =
  'pending' | 'applying' | 'applied' | 'failed' | 'discarded' | 'expired';

/** What this card decided locally: an apply outcome, a discard, or nothing yet. */
export type PreviewDecision = HarnessActionApplyResult | 'discarded' | null;

/** Server row status → card status. */
export function statusFromRow(
  status: HarnessPendingActionStatus,
): PreviewCardStatus {
  switch (status) {
    case 'created':
      return 'pending';
    case 'applying':
      return 'applying';
    default:
      return status;
  }
}

/**
 * The card's state, from the most to the least authoritative source: the
 * decision this card made, an apply in flight, the server row (history
 * replay), and finally the deadline as it stood when the card mounted.
 */
export function derivePreviewCardStatus(input: {
  decision: PreviewDecision;
  applyPending: boolean;
  rowStatus: HarnessPendingActionStatus | undefined;
  expiresAt: string;
  mountedAt: number;
}): PreviewCardStatus {
  if (input.decision === 'discarded') return 'discarded';
  if (input.decision) return input.decision.status;
  if (input.applyPending) return 'applying';
  if (input.rowStatus) return statusFromRow(input.rowStatus);
  const expiredAtMount = new Date(input.expiresAt).getTime() < input.mountedAt;
  return expiredAtMount ? 'expired' : 'pending';
}

/** The apply outcome to show, when this card applied (or failed to apply). */
export function appliedOutcome(
  decision: PreviewDecision,
): HarnessActionApplyResult | null {
  return decision && decision !== 'discarded' ? decision : null;
}
